import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { generateInsights, generateInsightsLive, InsightsError } from '../../../services/aiInsightsService';

/**
 * AIInsightsPanel, v3
 * ----------------------------------------------------------------------------
 * Fetches insights via aiInsightsService.generateInsights(), which today
 * returns pre-baked content but is shaped exactly like a live API call. The
 * service can be swapped to a real Claude endpoint without changing this
 * component.
 *
 * UX states this component renders:
 *   1. THINKING  , initial pause where a "model is reasoning" indicator
 *                   shows, before any content appears
 *   2. STREAMING , characters appear at variable speed with occasional
 *                   micro-pauses, mimicking real LLM streaming
 *   3. SETTLED   , full content visible plus recommendations and metadata
 *   4. ERROR     , fetch failed; show retry affordance
 */

// --- Tunables ---------------------------------------------------------------

const THINKING_MIN_MS = 600;   // Minimum thinking phase before streaming starts
const STREAM_BASE_MS = 8;      // Base ms per character
const STREAM_VARIANCE_MS = 8;  // Random ±variance per character
const PUNCTUATION_PAUSE_MS = 90; // Extra pause after . , ; :

// --- Hooks ------------------------------------------------------------------

/**
 * Stream text character-by-character with variable pacing, simulating
 * real LLM streaming. Returns the currently visible substring and a
 * boolean indicating whether streaming is complete.
 */
const useStreamedText = (fullText, isActive, onComplete) => {
  const [shown, setShown] = useState(isActive ? '' : fullText);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      setShown(fullText);
      return;
    }
    if (!fullText) {
      setShown('');
      return;
    }

    completedRef.current = false;
    setShown('');
    let cancelled = false;
    let i = 0;

    const tick = () => {
      if (cancelled) return;
      i += 1 + Math.floor(Math.random() * 2); // 1–2 chars per tick
      if (i >= fullText.length) {
        setShown(fullText);
        completedRef.current = true;
        onComplete?.();
        return;
      }
      setShown(fullText.slice(0, i));

      const ch = fullText[i - 1];
      const isPunctuation = ',.;:'.includes(ch);
      const delay = STREAM_BASE_MS
        + Math.random() * STREAM_VARIANCE_MS
        + (isPunctuation ? PUNCTUATION_PAUSE_MS : 0);
      setTimeout(tick, delay);
    };

    const initialDelay = setTimeout(tick, 50);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
    };
  }, [fullText, isActive, onComplete]);

  return { shown, complete: completedRef.current };
};

// --- Subcomponents ---------------------------------------------------------

/** Thinking-stage indicator: three pulsing dots with a label */
const ThinkingIndicator = ({ tenantName }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-primary-solid animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-primary-solid animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-primary-solid animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span className="text-xs text-muted-foreground italic">
        {t('ai.thinking', { tenant: tenantName })}
      </span>
    </div>
  );
};

/** Caret that blinks at end of streaming text */
const StreamingCaret = () => (
  <span
    className="inline-block w-[2px] h-[1em] align-text-bottom bg-primary-solid ml-0.5 animate-pulse"
    aria-hidden
  />
);

// --- Main component --------------------------------------------------------

const AIInsightsPanel = ({ tenant }) => {
  const { t, i18n } = useTranslation();
  const [stage, setStage] = useState('thinking'); // thinking | streaming | settled | error
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [requestStartTime, setRequestStartTime] = useState(null);

  /** Fetch insights, with cancellation guard so tenant switches don't race */
  const fetchInsights = useCallback(async (currentTenant, useLive = false) => {
    setStage('thinking');
    setInsights(null);
    setError(null);
    setRequestStartTime(Date.now());
    let cancelled = false;

    try {
      const data = await (useLive ? generateInsightsLive : generateInsights)(currentTenant, { lang: i18n.language });
      if (cancelled) return;
      setInsights(data);

      // Enforce a minimum "thinking" duration so the UX feels deliberate
      const elapsed = Date.now() - (requestStartTime || Date.now());
      const remaining = Math.max(0, THINKING_MIN_MS - elapsed);
      setTimeout(() => {
        if (!cancelled) setStage('streaming');
      }, remaining);
    } catch (e) {
      if (cancelled) return;
      const message = e instanceof InsightsError ? e.message : 'Unable to generate insights';
      setError(message);
      setStage('error');
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Re-fetch whenever the tenant OR language changes
  useEffect(() => {
    fetchInsights(tenant);
  }, [tenant.id, i18n.language, fetchInsights]);

  const onHeadlineComplete = useCallback(() => {
    // Headline finished, body keeps streaming, recommendations appear after
  }, []);

  const onBodyComplete = useCallback(() => {
    setStage('settled');
  }, []);

  const isStreaming = stage === 'streaming';
  const isSettled = stage === 'settled';

  const headlineStream = useStreamedText(
    insights?.headline || '',
    isStreaming,
    onHeadlineComplete
  );

  const bodyStream = useStreamedText(
    insights?.body || '',
    isStreaming,
    onBodyComplete
  );

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-card">
      {/* Decorative gradient corner */}
      <div
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #14B8A6 0%, transparent 70%)' }}
      />

      {/* Header */}
      <div className="relative p-5 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="relative shrink-0">
              <div className="p-2 rounded-md bg-gradient-to-br from-teal-500 to-teal-700">
                <Icon name="Sparkles" size={16} color="white" />
              </div>
              {(stage === 'thinking' || stage === 'streaming') && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-sm font-semibold text-foreground">{t('ai.title')}</h3>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
                  Claude Opus 4.7
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {stage === 'thinking' && t('ai.analysing', { tenant: tenant.name })}
                {stage === 'streaming' && t('ai.streaming', { tenant: tenant.name })}
                {stage === 'settled' && t('ai.complete', { tenant: tenant.name })}
                {stage === 'error' && t('ai.errorTitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => fetchInsights(tenant, true)}
            disabled={stage === 'thinking' || stage === 'streaming'}
            className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-primary-solid hover:bg-primary/10 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed border border-primary/20"
            title={t('ai.regenerateLive')}
          >
            <Icon name="Sparkles" size={12} className={stage === 'thinking' || stage === 'streaming' ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{t('ai.regenerateLive')}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-5 min-h-[180px]">
        {stage === 'thinking' && <ThinkingIndicator tenantName={tenant.name} />}

        {stage === 'error' && (
          <div className="flex items-start gap-3 py-2">
            <Icon name="AlertCircle" size={16} className="text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">
                {t('ai.errorTitle')}
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                {error}
              </p>
              <button
                type="button"
                onClick={() => fetchInsights(tenant, true)}
                className="text-xs font-semibold text-primary-solid hover:underline"
              >
                {t('common.tryAgain')} →
              </button>
            </div>
          </div>
        )}

        {(stage === 'streaming' || stage === 'settled') && insights && (
          <div className="space-y-4">
            {/* Headline */}
            <p className="text-base font-semibold text-foreground leading-snug">
              {headlineStream.shown}
              {isStreaming && headlineStream.shown.length < insights.headline.length && <StreamingCaret />}
            </p>

            {/* Body */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {bodyStream.shown}
              {isStreaming && bodyStream.shown.length < insights.body.length && <StreamingCaret />}
            </p>

            {/* Recommendations, appear only after body has fully streamed */}
            {isSettled && (
              <div className="pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex items-center gap-2 mb-2.5">
                  <Icon name="ListChecks" size={14} className="text-primary-solid" />
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">
                    {t('ai.recommendations')}
                  </h4>
                </div>
                <ol className="space-y-2.5">
                  {insights.recommendations.map((rec, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-sm text-foreground leading-snug"
                      style={{ animation: `fadeInUp 0.4s ease-out ${i * 80}ms both` }}
                    >
                      <span className="shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary-solid text-[10px] font-bold mt-0.5">
                        {i + 1}
                      </span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="relative px-5 py-3 bg-muted/50 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Icon name="Shield" size={12} />
            <span>{t('ai.footerSecurity')}</span>
          </div>
          {isSettled && insights?.meta && (
            <>
              <span className="text-border">·</span>
              <span className="font-mono text-[10px]">{t('ai.tokens', { count: insights.meta.tokenEstimate })}</span>
            </>
          )}
        </div>
        {isSettled && insights?.meta && (
          <span className="font-mono text-[10px]">
            {new Date(insights.meta.generatedAt).toLocaleTimeString(i18n.language === 'de' ? 'de-DE' : 'en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        )}
      </div>

      {/* Inline keyframes, Tailwind doesn't ship these by default */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AIInsightsPanel;
